import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Typography,
    Grid,
    Box,
    CircularProgress,
    Card,
    Divider,
    ListItemText,
    Avatar,
    ListItemAvatar,
    ListItem,
    List,
    Badge,
    Button,
    IconButton,
    Menu,
    MenuItem,
    Dialog,
    DialogActions,
    DialogTitle
} from '@mui/material';
import dayjs from 'dayjs';
import { t } from 'i18next';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { SelectionButton } from '../../../component/buttons';
import { useChats, useMarkAsSeen } from '../../../hooks/useChat';
import { getPanelRole } from '../../../services/api';
import { useUsers } from '../../../hooks/useUsers';

const MessageActions = ({ unseenUuids, pacienteNome }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [loading, setLoading] = useState(false);

    const { mutateAsync: markAsSeen } = useMarkAsSeen();

    const handleMenuOpen = (event) => setAnchorEl(event.currentTarget);
    const handleMenuClose = () => setAnchorEl(null);

    const handleMarkClick = () => {
        handleMenuClose();
        setConfirmOpen(true);
    };

    const handleConfirm = async () => {
        if (!unseenUuids?.length) return;
        setLoading(true);
        try {
            await markAsSeen(unseenUuids);
        } catch (err) {
            console.error('Erro ao marcar como lida:', err);
        } finally {
            setLoading(false);
            setConfirmOpen(false);
        }
    };

    return (
        <>
            {loading ? (
                <CircularProgress size={22} />
            ) : (
                <IconButton
                    size="small"
                    onClick={(e) => {
                        e.stopPropagation();
                        handleMenuOpen(e);
                    }}
                >
                    <MoreVertIcon />
                </IconButton>
            )}

            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                onClick={(e) => e.stopPropagation()}
            >
                <MenuItem
                    onClick={(e) => {
                        e.stopPropagation();
                        handleMarkClick();
                    }}
                >
                    {t('messages.mark_as_read')}
                </MenuItem>
            </Menu>

            <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
                <DialogTitle>
                    {t('messages.confirm_mark_title', { name: pacienteNome })}
                </DialogTitle>
                <DialogActions>
                    <Button onClick={() => setConfirmOpen(false)}>
                        {t('messages.cancel')}
                    </Button>
                    <Button onClick={handleConfirm} color="primary" autoFocus>
                        {t('messages.confirm')}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

const Messages = () => {
    const navigate = useNavigate();
    const [selected, setSelected] = useState(0);
    const { data: chats, isLoading } = useChats(true);
    const roleUUID = getPanelRole();
    const [openNewChat, setOpenNewChat] = useState(false);

    const conversations = useMemo(() => {
        if (!chats?.length) return [];

        const map = new Map();

        chats.forEach((msg) => {
            const isMeSender = msg.sender?.uuid === roleUUID;
            const other = isMeSender ? msg.receiver : msg.sender;
            const id = other?.uuid;

            const isUnreadForMe = !msg.seen && msg.receiver?.uuid === roleUUID;
            const isUnseenByOther = !msg.seen && msg.sender?.uuid === roleUUID;

            if (!id) return;

            if (!map.has(id)) {
                map.set(id, {
                    pacienteId: id,
                    pacienteNome: other?.name ?? t('messages.no_name'),
                    pacienteFoto: null,
                    ultimaMensagem: msg.message,
                    dataUltima: msg.sent_at,
                    naoLidas: isUnreadForMe ? 1 : 0,
                    unseenUuids: isUnreadForMe ? [msg.uuid] : [],
                    enviadasNaoVistas: isUnseenByOther ? 1 : 0
                });
            } else {
                const conv = map.get(id);

                if (new Date(msg.sent_at) > new Date(conv.dataUltima)) {
                    conv.ultimaMensagem = msg.message;
                    conv.dataUltima = msg.sent_at;
                }

                if (isUnreadForMe) {
                    conv.naoLidas += 1;
                    conv.unseenUuids.push(msg.uuid);
                }

                if (isUnseenByOther) {
                    conv.enviadasNaoVistas += 1;
                }
            }
        });

        return Array.from(map.values()).sort(
            (a, b) => new Date(b.dataUltima) - new Date(a.dataUltima)
        );
    }, [chats, roleUUID]);

    const sections = [
        t('messages.sections.unread'),
        t('messages.sections.send'),
        t('messages.sections.all')
    ];

    const unreadConversations = conversations.filter((c) => c.naoLidas > 0);

    const sentNotSeenConversations = conversations.filter((c) => c.enviadasNaoVistas > 0);

    const displayed = useMemo(() => {
        switch (selected) {
            case 0:
                return unreadConversations;
            case 1:
                return sentNotSeenConversations;
            case 2:
                return conversations;
            default:
                return conversations;
        }
    }, [selected, unreadConversations, sentNotSeenConversations, conversations]);

    const {
        data: patients,
        isLoading: loadingPatients,
        paginationModel,
        setPaginationModel,
        count,
        rowsPerPageOptions
    } = useUsers(['PATIENT'], '');

    return (
        <Card
            variant="outlined"
            sx={{
                p: 4,
                m: 2,
                borderRadius: 3,
                transition: 'all 0.3s ease'
            }}
        >
            <Box display="flex" width="100%" mb={2}>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={() => setOpenNewChat(true)}
                >
                    {t('messages.new_chat')}
                </Button>
            </Box>
            <Grid container spacing={3}>
                {sections.map((label, index) => (
                    <Grid item xs={4} key={index}>
                        <SelectionButton
                            selected={selected === index}
                            sx={{ width: '100%', py: 2 }}
                            onClick={() => setSelected(index)}
                        >
                            {label}
                        </SelectionButton>
                    </Grid>
                ))}

                {isLoading ? (
                    <Grid item xs={12}>
                        <Box display="flex" justifyContent="center" height="300px">
                            <CircularProgress sx={{ color: '#1976d2' }} />
                        </Box>
                    </Grid>
                ) : (
                    <Grid item xs={12}>
                        {displayed.length > 0 ? (
                            <List sx={{ width: '100%', bgcolor: 'background.paper' }}>
                                {displayed.map((conv, index) => (
                                    <React.Fragment key={conv.pacienteId}>
                                        <ListItem
                                            alignItems="center"
                                            sx={{
                                                borderRadius: 2,
                                                backgroundColor:
                                                    conv.naoLidas > 0
                                                        ? 'rgba(25, 118, 210, 0.08)'
                                                        : 'transparent',
                                                '&:hover': {
                                                    backgroundColor:
                                                        'rgba(25, 118, 210, 0.15)',
                                                    cursor: 'pointer'
                                                }
                                            }}
                                            onClick={() =>
                                                navigate(`/mensagens/${conv.pacienteId}`)
                                            }
                                        >
                                            <ListItemAvatar>
                                                <Badge
                                                    color="error"
                                                    overlap="circular"
                                                    invisible={conv.naoLidas === 0}
                                                    variant="dot"
                                                >
                                                    <Avatar
                                                        alt={conv.pacienteNome}
                                                        src={conv.pacienteFoto || ''}
                                                    />
                                                </Badge>
                                            </ListItemAvatar>

                                            <ListItemText
                                                primary={
                                                    <Box
                                                        display="flex"
                                                        justifyContent="space-between"
                                                        alignItems="center"
                                                    >
                                                        <Typography
                                                            variant="subtitle1"
                                                            fontWeight="bold"
                                                            noWrap
                                                        >
                                                            {conv.pacienteNome}
                                                        </Typography>
                                                        <Typography
                                                            variant="caption"
                                                            color="text.secondary"
                                                        >
                                                            {conv.dataUltima
                                                                ? dayjs(
                                                                      conv.dataUltima
                                                                  ).format('DD/MM HH:mm')
                                                                : '-'}
                                                        </Typography>
                                                    </Box>
                                                }
                                                secondary={
                                                    <Typography
                                                        variant="body2"
                                                        color="text.secondary"
                                                        noWrap
                                                    >
                                                        {conv.ultimaMensagem}
                                                    </Typography>
                                                }
                                            />
                                            {conv.naoLidas > 0 && (
                                                <MessageActions
                                                    unseenUuids={conv.unseenUuids}
                                                    pacienteNome={conv.pacienteNome}
                                                />
                                            )}
                                        </ListItem>

                                        {index < displayed.length - 1 && (
                                            <Divider sx={{ mt: 1, mb: 1 }} />
                                        )}
                                    </React.Fragment>
                                ))}
                            </List>
                        ) : (
                            <Typography
                                variant="body1"
                                color="text.secondary"
                                align="center"
                                sx={{ mt: 4 }}
                            >
                                {t('messages.no_messages')}
                            </Typography>
                        )}
                    </Grid>
                )}
            </Grid>
            <Dialog
                open={openNewChat}
                onClose={() => setOpenNewChat(false)}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle
                    sx={{
                        fontSize: '1.2rem',
                        fontWeight: 'bold'
                    }}
                >
                    {t('messages.select_patient')}
                </DialogTitle>

                <Box p={2}>
                    {loadingPatients ? (
                        <Box display="flex" justifyContent="center">
                            <CircularProgress />
                        </Box>
                    ) : (
                        <>
                            <List>
                                {patients.map((p) => (
                                    <ListItem
                                        key={p.uuid}
                                        button
                                        onClick={() => {
                                            setOpenNewChat(false);
                                            navigate(`/mensagens/${p.role_uuid}`);
                                        }}
                                    >
                                        <ListItemAvatar>
                                            <Avatar>{p.name?.[0]?.toUpperCase()}</Avatar>
                                        </ListItemAvatar>
                                        <ListItemText
                                            primary={p.name}
                                            secondary={p.email}
                                        />
                                    </ListItem>
                                ))}
                            </List>

                            <Box display="flex" justifyContent="space-between" mt={2}>
                                <Button
                                    disabled={paginationModel.page === 0}
                                    onClick={() =>
                                        setPaginationModel((prev) => ({
                                            ...prev,
                                            page: prev.page - 1
                                        }))
                                    }
                                >
                                    {t('messages.previous')}
                                </Button>

                                <Typography variant="body2">
                                    {paginationModel.page + 1} /{' '}
                                    {Math.ceil(count / paginationModel.pageSize)}
                                </Typography>

                                <Button
                                    disabled={
                                        (paginationModel.page + 1) *
                                            paginationModel.pageSize >=
                                        count
                                    }
                                    onClick={() =>
                                        setPaginationModel((prev) => ({
                                            ...prev,
                                            page: prev.page + 1
                                        }))
                                    }
                                >
                                    {t('messages.next')}
                                </Button>
                            </Box>
                        </>
                    )}
                </Box>

                <DialogActions>
                    <Button onClick={() => setOpenNewChat(false)} sx={{ mr: 1 }}>
                        {t('messages.cancel')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Card>
    );
};

export default Messages;
